import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Button, Grid, Heading, Message, Text } from '@afterpaytouch/core'
import FormBuilder from '@afterpaytouch/form'
import { Country } from '@afterpaytouch/portal-api/types'
import { useTranslation } from 'next-i18next'
import styles from './style.module.css'

import { getSchemaAndFormData, VerifyRetryForm, transformToBackend, getKeysToIgnoreInCompare } from './utils'
import { compareObjects } from '../../../utils/comparison'
import { validAddressOption } from './validations'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import {
  useAppRouter,
  useConsumerAccountCountry,
  useConsumerHasPreferredPaymentMethod,
  useConsumerSelector,
  useUpdateAccountDetailMutation,
} from '../../../state'
import { useConsumerSizes } from '../../../utils/responsive'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { Disclaimer } from '../../Disclaimer'
import { TrackingEvent } from '../../../model/amplitude'
import { VerifyLaterModal } from '../VerifyLaterModal/VerifyLaterModal'
import { SupportedLocale } from '@afterpay/types'
import { Route } from '../../../config/router'
import { isFetchBaseQueryError, isPortalApiError } from '@afterpaytouch/portal-api'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { ProfileErrorCode, ProfileErrors } from '../../../model'
import { useIdentityVerifyRedirect } from '../../../hooks/useIdentityVerifyRedirect/useIdentityVerifyRedirect'

export const VerifyRetry: FunctionComponent = () => {
  const { t } = useTranslation()
  const { textSize, buttonSize, headingSize } = useConsumerSizes()
  const consumer = useConsumerSelector()
  const country = useConsumerAccountCountry()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const router = useAppRouter()
  const { verifyAndHandleOutcome } = useIdentityVerifyRedirect()

  const [hasError, setHasError] = useState(false)
  const [hasAddressError, setHasAddressError] = useState(false)
  const [hasResponseError, setHasResponseError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showVerifyLaterModal, setShowVerifyLaterModal] = useState(false)
  const [updateAccountDetails, { isError: isUpdateAccountDetailsError, isSuccess: isUpdateAccountDetailsSuccess, error: updateAccountDetailsError }] =
    useUpdateAccountDetailMutation()
  const [profileError, setProfileError] = useState<ProfileErrorCode>()
  const isSameProfileInfoWarning = profileError === ProfileErrorCode.SameProfileInfoWarning
  const hasPreferredPaymentMethod = useConsumerHasPreferredPaymentMethod()

  const handleVerifyLaterClick = (): void => {
    logEvent(TrackingEvent.PRESSED_VERIFY_LATER_BUTTON)
    setShowVerifyLaterModal(true)
  }

  const { schema, initialFormData } = useMemo(() => {
    const renderError = (): JSX.Element => {
      let errorMessage = t('verifyIdentity:error:unableToUpdateAccountDetails')
      if (profileError === ProfileErrorCode.InvalidAddress) {
        errorMessage = t('verifyIdentity:error:invalidAddress')
      }
      return (
        <Grid>
          <Text color='Fire' size='XS' testNameSpace='verify-retry-error'>
            {errorMessage}
          </Text>
        </Grid>
      )
    }

    const components = {
      profileError: () => renderError(),
      footer: () => {
        return (
          <>
            <Disclaimer />
            <Grid>
              <Button.Ghost testNameSpace='verify-retry-verify-later' disabled={isLoading} onClick={handleVerifyLaterClick} padding='Fluid'>
                {t('verifyIdentity:verifyLater:buttonText')}
              </Button.Ghost>
            </Grid>
            <Grid>
              <Button.Primary testNameSpace='verify-retry-cta' loading={isLoading} disabled={isLoading} type='submit' padding='Fluid'>
                {t('verifyIdentity:retry:cta')}
              </Button.Primary>
            </Grid>
          </>
        )
      },
    }

    return getSchemaAndFormData(consumer, components, t, country, hasAddressError, profileError)
  }, [consumer, t, buttonSize, country, isLoading, hasAddressError, profileError, handleVerifyLaterClick])

  const [formData, setFormData] = useState<VerifyRetryForm>(initialFormData)

  const isInvalidForm = (data: VerifyRetryForm): boolean => {
    const areFormValuesSameAsProfile = compareObjects(initialFormData, data, true, getKeysToIgnoreInCompare(country))
    if (areFormValuesSameAsProfile) {
      setProfileError(ProfileErrorCode.SameProfileInfoWarning)
    }
    const isValidAddress = !validAddressOption(formData?.address, country)
    setHasAddressError(isValidAddress)
    return areFormValuesSameAsProfile || hasError || isValidAddress
  }

  const handleOnChange = (data: VerifyRetryForm): void => {
    setFormData(data)
    if (hasAddressError) {
      setHasAddressError(false)
    }
    if (isSameProfileInfoWarning) {
      const areFormValuesSameAsProfile = compareObjects(initialFormData, data, true, getKeysToIgnoreInCompare(country))
      if (!areFormValuesSameAsProfile) {
        setProfileError(undefined)
      }
    }
  }

  const handleStateChange = (isValid: boolean): void => {
    setHasError(!isValid)
  }

  const handleModalClose = (): void => {
    setShowVerifyLaterModal(false)
  }

  const handleVerifyNowClick = (): void => {
    logEvent(TrackingEvent.PRESSED_VERIFY_NOW_BUTTON)
    setShowVerifyLaterModal(false)
  }

  const handleSkipClick = (): void => {
    logEvent(TrackingEvent.PRESSED_SKIP_BUTTON)
    setShowVerifyLaterModal(false)
    router.push(Route.HOME)
  }

  const handleSubmit = async (inputData: VerifyRetryForm): Promise<void> => {
    const data = transformToBackend(inputData)
    logEvent(TrackingEvent.PRESSED_SUBMIT_BUTTON_ON_VERIFY_RETRY_PAGE)

    if (isInvalidForm(data)) {
      return
    }

    setIsLoading(true)
    const requestFormData = {
      ...data,
      ...data.address,
      countryCode: (data?.address?.countryCode ?? country) as Country,
      preferredLocale: consumer.preferredLocale as SupportedLocale,
      creditPolicyAgree: false,
    }

    try {
      await updateAccountDetails(requestFormData).unwrap()
      await verifyAndHandleOutcome({ showToastBody: !hasPreferredPaymentMethod })
      setIsLoading(false)
    } catch {
      setIsLoading(false)
      setHasResponseError(true)
    }
  }

  useEffect(() => {
    if (isUpdateAccountDetailsError && updateAccountDetailsError !== null) {
      const errorCode: ErrorCode | null =
        isFetchBaseQueryError(updateAccountDetailsError) && isPortalApiError(updateAccountDetailsError.data) ? updateAccountDetailsError.data.errorCode : null
      setProfileError(errorCode != null ? ProfileErrors[errorCode] ?? ProfileErrorCode.Default : ProfileErrorCode.Default)
    }
  }, [isUpdateAccountDetailsError, isUpdateAccountDetailsSuccess, updateAccountDetailsError])

  const renderGenericError = (): JSX.Element => {
    const warningHeading = t('verifyIdentity:error:generic:title')
    const warningDescription = (
      <Grid>
        <Text size='XS'>{t('verifyIdentity:error:generic:detail')}</Text>
      </Grid>
    )

    return (
      <div className={styles.messageContainer}>
        <Message testNameSpace='verify-retry-generic' kind='Error' iconName='Profile' heading={warningHeading} description={warningDescription} />
      </div>
    )
  }
  const renderSameProfileInfoWarning = (): JSX.Element => {
    const warningHeading = t('verifyIdentity:retry:sameProfileDetailsHeading')
    const warningDescription = (
      <Grid>
        <Text size='XS'>{t('verifyIdentity:retry:sameProfileDetailsDescription')}</Text>
      </Grid>
    )

    return (
      <div className={styles.messageContainer}>
        <Message testNameSpace='verify-retry-same-profile' kind='Info' iconName='WarningAlt' heading={warningHeading} description={warningDescription} />
      </div>
    )
  }

  useEffect(() => {
    if (typeof profileError !== 'undefined') {
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [profileError])

  return (
    <>
      <div className={styles.container}>
        <Grid>
          <Heading size={headingSize}>{t('verifyIdentity:retry:heading')}</Heading>
        </Grid>
        <div className='mb-2 mt-2'>
          <Text size={textSize}>{t('verifyIdentity:retry:description')}</Text>
        </div>
        {isSameProfileInfoWarning ? renderSameProfileInfoWarning() : hasResponseError ? renderGenericError() : null}
        <MaskedElement actionName='Verify Identity Form'>
          <FormBuilder
            schema={schema}
            onSubmit={handleSubmit}
            onChange={handleOnChange}
            onStateChange={handleStateChange}
            disabled={isLoading}
            testNameSpace='verify-retry'
          />
        </MaskedElement>
      </div>

      <VerifyLaterModal
        show={showVerifyLaterModal}
        onClose={handleModalClose}
        onVerifyNow={handleVerifyNowClick}
        onSkip={handleSkipClick}
        testNameSpace='verify-later'
        buttonSize={buttonSize}
      />
    </>
  )
}
